'use client';

import { useCookieConsentListener } from '@hooks/use-cookie-consent-listener';
import { analytics } from '@modules/analytics';
import { useFeatureFlag } from '@modules/feature-flag';
import type { UserDetails } from '@modules/user-profile/user-profile-details-validator';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { getAnalyticsRefPage } from '../page-ref';
import { zarazAnalytics } from '../zaraz-analytics';
import type { AnalyticsPagePropertiesInput, AnalyticsPlatforms } from '.cache/__types__';

const platforms = [
  'android',
  'api',
  'card_worker',
  'dash',
  'firebase_functions',
  'ios',
  'webflow',
  'intercom',
];
function isPlatform(candidate: unknown): candidate is AnalyticsPlatforms {
  return typeof candidate === 'string' && platforms.includes(candidate);
}

export const AnalyticsProvider = ({
  userProfileDetails,
}: {
  userProfileDetails: null | UserDetails;
}): JSX.Element => {
  const router = useRouter();

  // Get feature flags for analytics initialization
  const [zarazSwitchOff, { loading: zarazLoading }] = useFeatureFlag('zaraz_analytics_off', 'off');

  useCookieConsentListener();

  // Initialize analytics with feature flags when they're loaded
  useEffect(() => {
    if (!zarazLoading && zarazSwitchOff === 'off') {
      analytics.addDestination(zarazAnalytics);
    }
  }, [zarazSwitchOff, zarazLoading]);

  useEffect(() => {
    if (userProfileDetails?.id || userProfileDetails?.email) {
      analytics.identify({
        user_id: userProfileDetails.id,
        traits: {
          email: userProfileDetails.email,
        },
      });
    } else {
      analytics.identify({});
    }
  }, [userProfileDetails?.id, userProfileDetails?.email]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    const trackPageWithPath = (path: string) => {
      try {
        const url = new URL(path, 'https://blnq.xyz'); // use a fake baseURL
        const properties: AnalyticsPagePropertiesInput = {
          path: url.pathname,
          ref_page: getAnalyticsRefPage({
            pathname: url.pathname,
            searchParams: url.searchParams,
          }),
        };

        Object.entries(router.query).forEach(([key, value]) => {
          if (typeof key === 'string' && typeof value === 'string') {
            switch (key) {
              case 'utm_medium':
                properties[key] = value;
                break;
              case 'utm_source':
                properties[key] = value;
                break;
              case 'utm_campaign':
                properties[key] = value;
                break;
              case 'utm_content':
                properties[key] = value;
                break;
              case 'utm_term':
                properties[key] = value;
                break;
              case 'ref_original':
                properties[key] = value;
                break;
              case 'gclid':
                properties[key] = value;
                if (value) {
                  localStorage.setItem('gclid', value); // save for when user is created
                }
                break;
              case 'fbclid':
                properties[key] = value;
                if (value) {
                  localStorage.setItem('fbclid', value); // save for when user is created
                }
                break;
              case 'ref_path':
                properties[key] = value;
                break;
              case 'ref_cta':
                properties[key] = value;
                break;
              case 'ref_platform':
                if (isPlatform(value)) {
                  properties[key] = value;
                }
                break;
            }
          }
        });
        analytics.page({ properties });
      } catch (error) {
        console.warn('Page tracking failed', { error });
      }
    };

    if (router.isReady) {
      trackPageWithPath(router.asPath); // track pages after the router is ready
    }
  }, [router.asPath, router.isReady]);

  return <></>;
};
